/**
 * The note half of slot-to-slot dragging: building a payload from a note, and applying a move to it.
 *
 * Shared by the add-card panel and the Cards note dock, which have different shells around the same
 * two slots and must not answer the same gesture differently.
 *
 * **Every write here is a request**, unlike the reading log's, so ordering matters — see
 * `applyNoteSlotMove`.
 */

import { linkMedia, type NoteDetail } from "@/lib/note";

import type { SlotKey } from "./ImageSlots";
import { fieldForSlot } from "./mediaSlots";
import { planSlotMove, type SlotRef } from "./slotDrag";

/** Serialize one of a note's image slots for a drag, or null when it holds nothing. */
export function noteDragPayload(note: NoteDetail | null, which: SlotKey): string | null {
  if (!note) return null;
  const media = which === "extraImage" ? note.extraImage : note.image;
  if (!media?.filename) return null;
  const ref: SlotRef = {
    owner: { kind: "note", id: note.noteId },
    slot: which,
    value: media.raw,
    filename: media.filename,
  };
  return JSON.stringify(ref);
}

/**
 * Apply a drop onto one of `note`'s image slots. Returns the updated note, or null if nothing ran.
 *
 * **Only note→note drops are honoured.** A payload from a sentence is ignored rather than
 * half-applied: a sentence's slot is session state written synchronously and a note's is a row
 * written over HTTP, so a swap across that boundary can leave the local half repainted and the
 * remote half failed. See `slotDrag.ts`.
 *
 * **A swap is two sequential requests, and the failure between them is a duplicate rather than a
 * loss** — both fields end up holding the same image, which is a state the UI already permits and
 * the error line explains. That is why the target is written first: if only one write survives, the
 * one that survives is the one the gesture was aimed at.
 */
export async function applyNoteSlotMove(
  note: NoteDetail,
  which: SlotKey,
  payload: string,
): Promise<NoteDetail | null> {
  let from: SlotRef;
  try {
    from = JSON.parse(payload) as SlotRef;
  } catch {
    return null;
  }
  if (from.owner.kind !== "note" || from.owner.id !== note.noteId) return null;

  const target = which === "extraImage" ? note.extraImage : note.image;
  const move = planSlotMove(from, {
    owner: { kind: "note", id: note.noteId },
    slot: which,
    value: target?.raw ?? "",
    filename: target?.filename ?? null,
  });
  if (move.kind === "noop") return null;

  let updated: NoteDetail | null = null;
  for (const assignment of move.assign) {
    if (assignment.owner.kind !== "note") continue;
    // `assignment.filename`, never a fresh parse of `assignment.value` — see `SlotAssignment`.
    // Re-deriving it loses any image stored with a single-quoted `src`, by clearing it instead.
    //
    // There is no clear branch, and none is reachable: a copy needs a filled source and a swap
    // needs both sides filled, so every assignment names a real file. A drag cannot empty a slot.
    updated = await linkMedia(
      assignment.owner.id,
      fieldForSlot(assignment.slot),
      assignment.filename,
    );
  }
  return updated;
}
